import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowUp,
  FaCalendarAlt,
  FaChartArea,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaCheck,
  FaCheckCircle,
  FaChevronDown,
  FaChevronRight,
  FaClock,
  FaProjectDiagram,
  FaTimes,
  FaTimesCircle,
  FaWallet,
} from "react-icons/fa";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import { ExpenseStatus } from "../../types/expense";
import { ExpenseDetails } from "../../models/expense/ExpenseDetail";
import { formatNaira } from "../../utils/bill";
import { formatDate, formatDateForInput, getDateRange } from "../../utils/date";
import { sendRequest } from "../../utils/api";
import FullPageSpinner from "../../components/FullPageSpinner";
import { DateRangeEmptyState } from "../../components/EmptyState";


const formatCurrency = formatNaira;

const SummaryMetric = ({
  title,
  value,
  subtitle,
  icon,
  iconClass,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  iconClass: string;
}) => {
  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-slate-500">
            {title}
          </p>

          <p className="mt-1 truncate text-xl font-bold text-slate-800">
            {value}
          </p>

          <p className="mt-1 truncate text-[11px] text-slate-400">
            {subtitle}
          </p>
        </div>

        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconClass}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: ExpenseStatus }) => {
  const config = {
    draft: {
      label: "Draft",
      className: "bg-slate-100 text-slate-600",
    },
    pending: {
      label: "Pending",
      className: "bg-amber-50 text-amber-600",
    },
    approved: {
      label: "Approved",
      className: "bg-blue-50 text-blue-600",
    },
    rejected: {
      label: "Rejected",
      className: "bg-red-50 text-red-600",
    },
    paid: {
      label: "Paid",
      className: "bg-emerald-50 text-emerald-600",
    },
  }[status];

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${config.className}`}
    >
      {config.label}
    </span>
  );
};

const CategoryRow = ({
  category,
  amount,
  total,
}: {
  category: string;
  amount: number;
  total: number;
}) => {
  const percentage = total > 0 ? (amount / total) * 100 : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="truncate text-xs font-medium text-slate-600">
          {category}
        </span>

        <span className="shrink-0 text-xs font-semibold text-slate-700">
          {formatCurrency(amount)}
        </span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-blue-600 transition-all"
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      <p className="text-[10px] text-slate-400">
        {percentage.toFixed(1)}% of total spending
      </p>
    </div>
  );
};


const ExpenseSummaryPage = () => {
  const navigate = useNavigate();


  const [chartType, setChartType] = useState<
    "pie" | "bar" | "line" | "area"
  >("pie");

  const [loading, setLoading] = useState<boolean>(false);
  const [exp, setExp] = useState<ExpenseDetails[]>([]);

  const [showCustomDates, setShowCustomDates] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  const getExpense = async (startDate: string, endDate: string) => {
    try {
      const { start, end } = getDateRange("month");

      const begin = formatDateForInput(start as Date);
      const last = formatDateForInput(end as Date);
      setLoading(true);

      setStartDate("");
      setEndDate("");
      setShowCustomDates(false);

      const expenses = await sendRequest(
        "GET",
        "/expenses?" +
        `startDate=${startDate}` +
        `&endDate=${endDate}`
      );


      const isComplete = startDate && endDate;
      setAppliedStartDate(isComplete ? startDate : begin);
      setAppliedEndDate(isComplete ? endDate : last);
      
      setExp(expenses);
    } catch (err: any) {

    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getExpense(startDate, endDate);
  }, []);

  const statistics = useMemo(() => {
    const totalRequested = exp.reduce(
      (sum, { expense }) => sum + expense?.requestedAmount,
      0,
    );

    const totalApproved = exp.reduce(
      (sum, { expense }) => sum + (expense?.approvedAmount ?? 0),
      0,
    );

    const pending = exp
      .filter(({ expense }) => expense?.status === "pending")
      .reduce((sum, expense) => sum + expense.expense.requestedAmount, 0);

    const rejected = exp
      .filter(({ expense }) => expense.status === "rejected")
      .reduce((sum, { expense }) => sum + expense.requestedAmount, 0);

    const approvedCount = exp.filter(
      ({ expense }) =>
        expense.status === "approved"
    ).length;

    const approvalRate =
      exp.length > 0
        ? (approvedCount / exp.length) * 100
        : 0;

    return {
      totalRequested,
      totalApproved,
      // totalPaid,
      pending,
      rejected,
      approvalRate,
    };
  }, [exp]);

  const categoryStats = useMemo(() => {
    const grouped: Record<string, number> = {};

    exp.filter(expense => expense.expense.status === "approved").forEach(({ expense }) => {
      grouped[expense.category] =
        (grouped[expense.category] || 0) +
        (expense.approvedAmount ?? expense.requestedAmount);
    });

    return Object.entries(grouped)
      .map(([category, amount]) => ({
        category,
        amount,
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [exp]);

  const getPercentage = (value: number, total: number): number => {
    if (total <= 0 || value <= 0) return 0;
    return Math.min((value / total) * 100, 100);
  };


  const recentExpenses = exp.slice(0, 5);

  return (
    <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-slate-50">
      <main className="mx-auto w-full min-w-0 max-w-7xl px-6 py-6 sm:px-7 lg:px-8">
        <FullPageSpinner show={loading} />
        {/* Header */}
        <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => navigate("/expenses")}
              className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
            >
              {FaArrowLeft({
                size: 13,
                className: "",
              })}
            </button>

            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Expense Summary
              </h1>

              <p className="mt-1 text-xs text-slate-400">
                Monitor spending, approvals and payments
              </p>
            </div>
          </div>


          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCustomDates((prev) => !prev)}
              className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:border-blue-300 hover:bg-blue-50/50"
            >
              {FaCalendarAlt({ size: 12, className: "text-blue-600" })}

              <span>
                {appliedStartDate && appliedEndDate
                  ? `${formatDate(appliedStartDate)}  →  ${formatDate(appliedEndDate)}`
                  : "Select date range"}
              </span>

              {FaChevronDown({ size: 10, className: "text-slate-400" })}
            </button>

            {showCustomDates && (
              <>
                <button
                  type="button"
                  aria-label="Close date range dropdown"
                  onClick={() => setShowCustomDates(false)}
                  className="fixed inset-0 z-40 cursor-default"
                />

                <div className="absolute right-0 top-full z-50 mt-2 w-[min(320px,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
                  <h3 className="mb-1 text-sm font-semibold text-slate-800">
                    Custom Date Range
                  </h3>
                  <p className="mb-4 text-xs text-slate-400">
                    Select the dates for your expense report.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label
                        htmlFor="expense-start-date"
                        className="mb-1 block text-xs font-medium text-slate-600"
                      >
                        Start Date
                      </label>
                      <input
                        id="expense-start-date"
                        type="date"
                        value={startDate}
                        max={endDate || undefined}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="h-10 w-full rounded-lg border border-slate-200 px-3 text-xs outline-none focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="expense-end-date"
                        className="mb-1 block text-xs font-medium text-slate-600"
                      >
                        End Date
                      </label>
                      <input
                        id="expense-end-date"
                        type="date"
                        value={endDate}
                        min={startDate || undefined}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="h-10 w-full rounded-lg border border-slate-200 px-3 text-xs outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                        setShowCustomDates(false);
                      }}
                      className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      disabled={!startDate || !endDate || startDate > endDate}
                      onClick={() => {
                        getExpense(startDate, endDate);

                      }}
                      className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Apply Dates
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Main Metrics */}
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
          <SummaryMetric
            title="Total Requested"
            value={formatCurrency(statistics.totalRequested)}
            subtitle={`${exp.length} expense claims`}
            icon={FaWallet({
              size: 17,
              className: "text-blue-600",
            })}
            iconClass="bg-blue-50"
          />

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50">
                {FaArrowUp({
                  size: 14,
                  className: "text-emerald-600",
                })}
              </div>

              <div>
                <p className="text-xs font-medium text-slate-500">
                  Approval Rate
                </p>

                <p className="mt-1 text-lg font-bold text-slate-800">
                  {statistics.approvalRate.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="mt-4 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SummaryMetric
            title="Approved"
            value={formatCurrency(statistics.totalApproved)}
            subtitle="Management approved"
            icon={FaCheckCircle({
              size: 17,
              className: "text-emerald-600",
            })}
            iconClass="bg-emerald-50"
          />

          <SummaryMetric
            title="Pending"
            value={formatCurrency(statistics.pending)}
            subtitle="Awaiting approval"
            icon={FaClock({
              size: 17,
              className: "text-amber-600",
            })}
            iconClass="bg-amber-50"
          />

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50">
                {FaTimesCircle({
                  size: 14,
                  className: "text-red-500",
                })}
              </div>

              <div>
                <p className="text-xs font-medium text-slate-500">
                  Rejected
                </p>

                <p className="mt-1 text-lg font-bold text-slate-800">
                  {formatCurrency(statistics.rejected)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics */}
        <div className="mt-6 grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Category Breakdown */}
          <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-800">
                  Approved Spending by Category
                </h2>

                <p className="mt-1 text-[11px] text-slate-400">
                  Where expense requests are going
                </p>
              </div>

              <div className="flex h-10 w-35 items-center justify-center rounded-lg bg-blue-50">
                {/* Chart Type Switcher */}
                <button
                  type="button"
                  title="Pie chart"
                  aria-label="Show pie chart"
                  aria-pressed={chartType === "pie"}
                  onClick={() => setChartType("pie")}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition ${chartType === "pie"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-400 hover:bg-white hover:text-slate-700"
                    }`}
                >
                  {FaChartPie({ size: 14 })}
                </button>

                <button
                  type="button"
                  title="Bar chart"
                  aria-label="Show bar chart"
                  aria-pressed={chartType === "bar"}
                  onClick={() => setChartType("bar")}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition ${chartType === "bar"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-400 hover:bg-white hover:text-slate-700"
                    }`}
                >
                  {FaChartBar({ size: 14 })}
                </button>

                <button
                  type="button"
                  title="Line graph"
                  aria-label="Show line graph"
                  aria-pressed={chartType === "line"}
                  onClick={() => setChartType("line")}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition ${chartType === "line"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-400 hover:bg-white hover:text-slate-700"
                    }`}
                >
                  {FaProjectDiagram({ size: 14 })}
                </button>
                <button
                  type="button"
                  title="Area chart"
                  aria-label="Show area chart"
                  aria-pressed={chartType === "area"}
                  onClick={() => setChartType("area")}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition ${chartType === "area"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-400 hover:bg-white hover:text-slate-700"
                    }`}
                >
                  {FaChartLine({ size: 16 })}
                </button>

              </div>
            </div>

            {categoryStats.length > 0 ? (
              <div className="h-[280px] w-full min-w-0">
                {chartType === "area" ? (<div className="space-y-5 p-5">
                  {categoryStats.map((item) => (
                    <CategoryRow
                      key={item.category}
                      category={item.category}
                      amount={item.amount}
                      total={statistics.totalApproved}
                    />
                  ))}
                </div>) : (<ResponsiveContainer width="100%" height="100%">
                  {chartType === "pie" ? (
                    <PieChart>
                      <Pie
                        data={categoryStats}
                        dataKey="amount"
                        nameKey="category"
                        cx="50%"
                        cy="45%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={3}
                        stroke="none"
                      >
                        {categoryStats.map((entry, index) => {
                          const COLORS = [
                            "#2563eb",
                            "#10b981",
                            "#f59e0b",
                            "#8b5cf6",
                            "#ef4444",
                            "#06b6d4",
                            "#f97316",
                            "#64748b",
                          ];

                          return (
                            <Cell
                              key={entry.category}
                              fill={COLORS[index % COLORS.length]}
                            />
                          );
                        })}
                      </Pie>

                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                        contentStyle={{
                          borderRadius: "10px",
                          border: "1px solid #e2e8f0",
                          fontSize: "12px",
                        }}
                      />

                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        wrapperStyle={{
                          fontSize: "11px",
                          paddingTop: "10px",
                        }}
                      />
                    </PieChart>
                  ) : chartType === "bar" ? (
                    <BarChart
                      data={categoryStats}
                      margin={{ top: 10, right: 10, left: 5, bottom: 35 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e2e8f0"
                      />

                      <XAxis
                        dataKey="category"
                        tick={{ fontSize: 10, fill: "#64748b" }}
                        angle={-25}
                        textAnchor="end"
                        interval={0}
                        axisLine={false}
                        tickLine={false}
                      />

                      <YAxis
                        tick={{ fontSize: 10, fill: "#64748b" }}
                        tickFormatter={(value) =>
                          `₦${Number(value).toLocaleString("en-NG")}`
                        }
                        axisLine={false}
                        tickLine={false}
                        width={65}
                      />

                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                        contentStyle={{
                          borderRadius: "10px",
                          border: "1px solid #e2e8f0",
                          fontSize: "12px",
                        }}
                      />

                      <Bar
                        dataKey="amount"
                        name="Requested Amount"
                        fill="#2563eb"
                        radius={[5, 5, 0, 0]}
                        maxBarSize={42}
                      />
                    </BarChart>
                  ) : (
                    <LineChart
                      data={categoryStats}
                      margin={{ top: 10, right: 10, left: 5, bottom: 35 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e2e8f0"
                      />

                      <XAxis
                        dataKey="category"
                        tick={{ fontSize: 10, fill: "#64748b" }}
                        angle={-25}
                        textAnchor="end"
                        interval={0}
                        axisLine={false}
                        tickLine={false}
                      />

                      <YAxis
                        tick={{ fontSize: 10, fill: "#64748b" }}
                        tickFormatter={(value) =>
                          `₦${Number(value).toLocaleString("en-NG")}`
                        }
                        axisLine={false}
                        tickLine={false}
                        width={65}
                      />

                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                        contentStyle={{
                          borderRadius: "10px",
                          border: "1px solid #e2e8f0",
                          fontSize: "12px",
                        }}
                      />

                      <Line
                        type="monotone"
                        dataKey="amount"
                        name="Requested Amount"
                        stroke="#2563eb"
                        strokeWidth={3}
                        dot={{
                          r: 4,
                          fill: "#2563eb",
                          strokeWidth: 2,
                          stroke: "#ffffff",
                        }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  )}
                </ResponsiveContainer>)}
              </div>
            ) : (
              <DateRangeEmptyState
                startDate={startDate}
                endDate={endDate}
              />
            )}
          </section>

          {/* Payment Overview */}
          <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-sm font-semibold text-slate-800">
                Payment Overview
              </h2>

              <p className="mt-1 text-[11px] text-slate-400">
                Requested, approved and paid amounts
              </p>
            </div>

            <div className="p-5">
              <div className="space-y-5">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">
                      Requested
                    </span>

                    <span className="text-xs font-semibold text-slate-700">
                      {formatCurrency(statistics.totalRequested)}
                    </span>
                  </div>

                  <div className="h-2 rounded-full bg-slate-100">
                    <div className={`h-full w-full rounded-full 
                    ${statistics.totalRequested > 0 ? "bg-blue-600" : "bg-slate-100"}`} />
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">
                      Pending
                    </span>

                    <span className="text-xs font-semibold text-slate-700">
                      {formatCurrency(statistics.pending)}
                    </span>
                  </div>

                  <div className="h-2 rounded-full bg-slate-100">
                    <div className="h-2 rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-yellow-500 transition-all duration-300"
                        style={{
                          width: `${getPercentage(
                            statistics.pending,
                            statistics.totalRequested
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">
                      Approved
                    </span>

                    <span className="text-xs font-semibold text-slate-700">
                      {formatCurrency(statistics.totalApproved)}
                    </span>
                  </div>

                  <div className="h-2 rounded-full bg-slate-100">
                    <div className="h-2 rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                        style={{
                          width: `${getPercentage(
                            statistics.totalApproved,
                            statistics.totalRequested
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">
                      Rejected
                    </span>

                    <span className="text-xs font-semibold text-slate-700">
                      {formatCurrency(statistics.rejected)}
                    </span>
                  </div>

                  <div className="h-2 rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-red-500 transition-all duration-300"
                      style={{
                        width: `${getPercentage(
                          statistics.rejected,
                          statistics.totalRequested
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Spending by Category */}
              <section className="min-w-0">

              </section>
            </div>
          </section>
        </div>

        {/* Recent Paid Expenses */}
        <section className="mt-6 min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">
                Recent Expenses
              </h2>

              <p className="mt-1 text-[11px] text-slate-400">
                Recently completed expense payments
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/expenses?status=paid")}
              className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              View all

              {FaChevronRight({
                size: 10,
                className: "",
              })}
            </button>
          </div>
          <div className="overflow-x-auto">
            {recentExpenses.length > 0 ? (
              <table className="w-full min-w-[320px] md:min-w-[700px]">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      Expense
                    </th>
                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      Category
                    </th>
                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      Amount
                    </th>
                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      Date
                    </th>
                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {recentExpenses.map(({ expense, requestedBy }) => (
                    <tr
                      key={expense.id}
                      onClick={() => navigate(`/expenses/${expense.id}`)}
                      className="group cursor-pointer border-b border-slate-100 transition-all duration-200 hover:bg-blue-50/60 hover:shadow-[inset_3px_0_0_0_#2563eb] active:bg-blue-100/60"
                    >
                      <td className="px-5 py-4">
                        <p className="text-xs font-semibold text-slate-700">
                          {expense.title}
                        </p>
                        <p className="mt-0.5 text-[10px] text-slate-400">
                          {requestedBy.firstName} {requestedBy.lastName}
                        </p>
                      </td>

                      <td className="px-5 py-4 text-xs text-slate-500">
                        {expense.category}
                      </td>

                      <td className="px-5 py-4 text-xs font-semibold text-slate-700">
                        {formatCurrency(
                          expense.approvedAmount ?? expense.requestedAmount
                        )}
                      </td>

                      <td className="px-5 py-4 text-xs text-slate-500">
                        {formatDate(expense.createdAt)}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center justify-between gap-3">
                          <StatusBadge status={expense.status} />
                          <span className="translate-x-0 text-blue-600 opacity-100 transition-all duration-200">
                            {FaChevronRight({ size: 12 })}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <DateRangeEmptyState
                startDate={startDate}
                endDate={endDate}
              />
            )}
          </div>

        </section>
      </main>
    </div>
  );
};

export default ExpenseSummaryPage;